/**
 * React Query hooks for Admin / Access Control
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as adminService from '@/services/admin.service';
import type { AddUserInput, AllowedUserUpdates, AddBlockInput, AiImportSettingsUpdate, InboundSettingsUpdate } from '@/services/admin.service';

// Query key factory
export const adminKeys = {
  all: ['admin'] as const,
  allowedUsers: () => [...adminKeys.all, 'allowed-users'] as const,
  accessCheck: (email: string) => [...adminKeys.all, 'access-check', email.toLowerCase()] as const,
  blocklist: () => [...adminKeys.all, 'blocklist'] as const,
  inboundSettings: () => [...adminKeys.all, 'inbound-settings'] as const,
  aiImportSettings: () => [...adminKeys.all, 'ai-import-settings'] as const,
};

// Hard ceiling on a single access check. Without this, a stalled Supabase
// request (e.g. an orphaned auth lock) leaves the query pending forever and the
// "Verifying access…" spinner can never resolve. Rejecting lets React Query
// retry, and ultimately surfaces a recoverable error instead of an infinite hang.
const ACCESS_CHECK_TIMEOUT_MS = 8000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Check if the current user has access to the app.
 *
 * `hasSession` MUST be true before the check runs: check_flow_access() reads
 * the caller's Supabase JWT email, so running it before a Supabase session
 * exists (e.g. a Microsoft-SSO user whose token exchange is still in flight)
 * sends the RPC on the anon key and spuriously reports 'unverifiable' — the
 * transient "Couldn't verify your access" screen. Gating on the session makes
 * the query fire exactly once the JWT can identify the user; React Query then
 * runs it automatically as soon as `enabled` flips true.
 */
export function useAccessCheck(email: string | undefined | null, hasSession: boolean) {
  return useQuery({
    queryKey: adminKeys.accessCheck(email || ''),
    // Returns 'allowed' | 'denied' | 'unverifiable' (fail-safe — a token that
    // can't be verified is recoverable, not a hard denial). See migration 119.
    queryFn: () => withTimeout(adminService.checkFlowAccess(), ACCESS_CHECK_TIMEOUT_MS, 'Access check'),
    enabled: !!email && hasSession,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  });
}

/**
 * Get all allowed users (admin only)
 */
export function useAllowedUsers() {
  return useQuery({
    queryKey: adminKeys.allowedUsers(),
    queryFn: adminService.getAllowedUsers,
    staleTime: 30 * 1000,
  });
}

/**
 * Add a user to the allowed list, and provision the Supabase auth account that
 * makes the authorization usable.
 *
 * These are two records that used to drift: allowed_users said "yes" while
 * auth.users had no row, and with signups disabled that combination locks the
 * person out of Flow entirely. Provisioning runs after the insert and its
 * failure is REPORTED, not thrown — the allowlist row is genuinely created
 * either way, and rolling it back would be a worse lie than a warning.
 */
export function useAddAllowedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ input, addedByEmail }: { input: AddUserInput; addedByEmail: string }) => {
      const user = await adminService.addAllowedUser(input, addedByEmail);
      const provision = await adminService.provisionAuthUser(user.email);
      return { user, provision };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.allowedUsers() });
    },
  });
}

/**
 * Remove (deactivate) a user
 */
export function useRemoveAllowedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.removeAllowedUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.allowedUsers() });
    },
  });
}

/** Edit a team member — name, role, or "Can email Flow" (allowed_users.can_submit_ai_imports). */
export function useUpdateAllowedUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: AllowedUserUpdates }) =>
      adminService.updateAllowedUser(id, updates),
    // Returned, so the mutation stays pending until the refetch lands: the row
    // never sits clickable on a stale value while the new one is in flight.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.allowedUsers() }),
  });
}

/**
 * Reactivate a user
 */
export function useReactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.reactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.allowedUsers() });
    },
  });
}

/**
 * Delete a user permanently
 */
export function useDeleteAllowedUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminService.deleteAllowedUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.allowedUsers() });
    },
  });
}

// =============================================================================
// INBOUND EMAIL — BLOCKLIST + INTAKE SETTINGS
// =============================================================================

export function useBlocklist() {
  return useQuery({
    queryKey: adminKeys.blocklist(),
    queryFn: adminService.getBlocklist,
    staleTime: 30 * 1000,
  });
}

export function useAddBlockEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddBlockInput) => adminService.addBlockEntry(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.blocklist() }),
  });
}

export function useDeactivateBlockEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminService.deactivateBlockEntry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.blocklist() }),
  });
}

export function useReactivateBlockEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminService.reactivateBlockEntry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.blocklist() }),
  });
}

export function useDeleteBlockEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminService.deleteBlockEntry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.blocklist() }),
  });
}

export function useInboundSettings() {
  return useQuery({
    queryKey: adminKeys.inboundSettings(),
    queryFn: adminService.getInboundSettings,
    staleTime: 30 * 1000,
  });
}

export function useUpdateInboundSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: InboundSettingsUpdate) => adminService.updateInboundSettings(updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.inboundSettings() }),
  });
}

/** The AI-import pipeline's stage switches (ai_import_settings): readable by every allowed user. */
export function useAiImportSettings() {
  return useQuery({
    queryKey: adminKeys.aiImportSettings(),
    queryFn: adminService.getAiImportSettings,
    staleTime: 30 * 1000,
  });
}

export function useUpdateAiImportSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: AiImportSettingsUpdate) => adminService.updateAiImportSettings(updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.aiImportSettings() }),
  });
}

// Admin identity is no longer an email helper — it is a server answer. Read it
// from usePermissions().isAdminUser, which sources check_flow_access().is_admin
// via useAccessCheck above (migration 181).
